/**
 * HYCO PDP sticky add-to-cart bar.
 * Visible when the main CTA scrolls above the viewport; syncs with buy-area selections.
 */
if (!customElements.get('hyco-pdp-sticky-atc')) {
  customElements.define(
    'hyco-pdp-sticky-atc',
    class HycoPdpStickyAtc extends HTMLElement {
      constructor() {
        super();
        this.mainCtaObserver = null;
        this.footerObserver = null;
        this.mainCtaVisible = true;
        this.footerVisible = false;
        this.onVariantChange = this.onVariantChange.bind(this);
        this.onVariantUpdated = this.onVariantUpdated.bind(this);
        this.onCartAdded = this.onCartAdded.bind(this);
        this.onSubmitClick = this.onSubmitClick.bind(this);
      }

      connectedCallback() {
        this.sectionId = this.dataset.sectionId || '';
        this.blockId = this.dataset.blockId || '';
        this.productInfo = document.querySelector(
          '.block-buy-area__pi[data-section-id="' + this.sectionId + '"]'
        );
        this.buyArea =
          this.productInfo ||
          document.querySelector(
            '[data-hyco-buy-area-anchor][data-buy-area-root="' +
              this.blockId +
              '"]'
          );

        if (!this.buyArea) {
          return;
        }

        if (this.parentElement !== document.body) {
          document.body.appendChild(this);
        }

        this.cacheElements();
        this.setupObservers();
        this.bindEvents();
        this.bindBuyAreaListeners();
        this.syncFromBuyArea();
        this.updateVisibility();
      }

      disconnectedCallback() {
        this.teardownObservers();
        this.unbindEvents();
      }

      cacheElements() {
        this.panel = this.querySelector('.hyco-pdp-sticky-atc__panel');
        this.imageEl = this.querySelector('[data-hyco-sticky-image]');
        this.summaryEl = this.querySelector('[data-hyco-sticky-summary]');
        this.compareEl = this.querySelector('[data-hyco-sticky-compare]');
        this.priceEl = this.querySelector('[data-hyco-sticky-price]');
        this.submitBtn = this.querySelector('[data-hyco-sticky-atc-submit]');
        this.submitText = this.querySelector('[data-hyco-sticky-atc-text]');
        this.loadingEl = this.querySelector('[data-hyco-sticky-atc-loading]');
      }

      getMainSubmitButton() {
        return this.productInfo?.querySelector('[name="add"]');
      }

      getVisibilityTarget() {
        return (
          this.getMainSubmitButton() ||
          this.buyArea?.querySelector('.product-form__buttons-group') ||
          this.buyArea
        );
      }

      setupMainCtaObserver() {
        if (this.mainCtaObserver) {
          this.mainCtaObserver.disconnect();
        }

        const visibilityTarget = this.getVisibilityTarget();
        if (!visibilityTarget) {
          return;
        }

        this.mainCtaObserver = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              this.mainCtaVisible =
                entry.isIntersecting || entry.boundingClientRect.top >= 0;
              this.updateVisibility();
            });
          },
          { threshold: 0, rootMargin: '0px' }
        );
        this.mainCtaObserver.observe(visibilityTarget);
      }

      setupObservers() {
        this.setupMainCtaObserver();

        const footer = document.querySelector('.footer-wrapper');
        if (footer) {
          this.footerObserver = new IntersectionObserver(
            (entries) => {
              entries.forEach((entry) => {
                this.footerVisible = entry.isIntersecting;
                this.updateVisibility();
              });
            },
            { threshold: 0.25, rootMargin: '0px' }
          );
          this.footerObserver.observe(footer);
        }
      }

      teardownObservers() {
        if (this.mainCtaObserver) {
          this.mainCtaObserver.disconnect();
        }
        if (this.footerObserver) {
          this.footerObserver.disconnect();
        }
      }

      bindEvents() {
        if (this.submitBtn) {
          this.submitBtn.addEventListener('click', this.onSubmitClick);
        }

        const bus =
          window.eventBus || (typeof eventBus !== 'undefined' ? eventBus : null);
        if (bus && bus.on) {
          bus.on('variant:change', this.onVariantChange);
          bus.on('variant:updated', this.onVariantUpdated);
          bus.on('cart:added', this.onCartAdded);
        }
      }

      unbindEvents() {
        if (this.submitBtn) {
          this.submitBtn.removeEventListener('click', this.onSubmitClick);
        }

        const bus =
          window.eventBus || (typeof eventBus !== 'undefined' ? eventBus : null);
        if (bus && bus.off) {
          bus.off('variant:change', this.onVariantChange);
          bus.off('variant:updated', this.onVariantUpdated);
          bus.off('cart:added', this.onCartAdded);
        }
      }

      onVariantChange(context) {
        if (
          context.sectionId &&
          String(context.sectionId) !== String(this.sectionId)
        ) {
          return;
        }
        this.syncFromBuyArea(context.variant);
      }

      onVariantUpdated(context) {
        if (
          context.sectionId &&
          String(context.sectionId) !== String(this.sectionId)
        ) {
          return;
        }
        this.syncFromBuyArea(context.variant);
        this.setupMainCtaObserver();
      }

      onCartAdded(evt) {
        if (
          evt &&
          evt.sectionId &&
          String(evt.sectionId) !== String(this.sectionId)
        ) {
          return;
        }
        this.setLoading(false);
      }

      bindBuyAreaListeners() {
        if (!this.buyArea || this.buyArea.dataset.stickyDelegated === 'true') {
          return;
        }

        this.buyArea.dataset.stickyDelegated = 'true';
        this.buyArea.addEventListener('change', (event) => {
          const target = event.target;
          if (!(target instanceof HTMLElement)) {
            return;
          }

          if (
            target.matches('[data-buy-area-plan-select]') ||
            target.matches(
              'input[name="buy-area-purchase-' + this.blockId + '"]'
            )
          ) {
            this.syncFromBuyArea();
          }
        });
      }

      onSubmitClick() {
        const mainBtn = this.getMainSubmitButton();
        if (!mainBtn || mainBtn.disabled) {
          return;
        }
        this.setLoading(true);
        mainBtn.click();
      }

      setLoading(isLoading) {
        if (!this.submitBtn) {
          return;
        }
        this.submitBtn.classList.toggle('is-loading', isLoading);
        if (this.submitText) {
          this.submitText.style.visibility = isLoading ? 'hidden' : '';
        }
        if (this.loadingEl) {
          this.loadingEl.classList.toggle('hidden', !isLoading);
        }
      }

      updateVisibility() {
        const show = !this.mainCtaVisible && !this.footerVisible;
        this.classList.toggle('is-visible', show);
        this.toggleAttribute('hidden', !show);
      }

      /**
       * @param {object | null | undefined} variant
       */
      syncFromBuyArea(variant) {
        if (!this.buyArea) {
          return;
        }

        const pi = this.productInfo;
        const parts = [];

        const subWrap = this.buyArea.querySelector(
          '[data-buy-area-sub="' + this.blockId + '"]'
        );
        if (subWrap) {
          const checked = subWrap.querySelector(
            'input[name="buy-area-purchase-' + this.blockId + '"]:checked'
          );
          const isSub = !checked || checked.value !== 'once';
          parts.push(isSub ? 'Subscribe & Save' : 'One Time Purchase');

          const packCell = this.buyArea.querySelector(
            '.block-buy-area__supply-cell.is-selected .block-buy-area__supply-title'
          );
          if (packCell && packCell.textContent.trim()) {
            parts.push(packCell.textContent.trim());
          }

          if (isSub) {
            const freqSelect = subWrap.querySelector(
              '[data-buy-area-plan-select]'
            );
            if (freqSelect && freqSelect.selectedOptions.length) {
              parts.push(freqSelect.selectedOptions[0].textContent.trim());
            }
          }

          const activePlan =
            subWrap.querySelector('.block-buy-area__plan.is-selected') ||
            (isSub
              ? subWrap.querySelector('.block-buy-area__plan--sub')
              : subWrap.querySelector('.block-buy-area__plan--once'));
          const prices = activePlan?.querySelector(
            '.block-buy-area__plan-prices'
          );
          if (prices && this.priceEl) {
            const now = prices.querySelector(
              '.block-buy-area__price-now, .block-buy-area__price-once'
            );
            const old = prices.querySelector('.block-buy-area__price-old');
            if (now) {
              this.priceEl.textContent = now.textContent.trim();
            }
            if (this.compareEl) {
              if (old && old.textContent.trim()) {
                this.compareEl.textContent = old.textContent.trim();
                this.compareEl.hidden = false;
              } else {
                this.compareEl.textContent = '';
                this.compareEl.hidden = true;
              }
            }
          }
        } else {
          const packCell = this.buyArea.querySelector(
            '.block-buy-area__supply-cell.is-selected .block-buy-area__supply-title'
          );
          if (packCell && packCell.textContent.trim()) {
            parts.push(packCell.textContent.trim());
          }
          if (this.priceEl) {
            const once = this.buyArea.querySelector(
              '.block-buy-area__price-once'
            );
            if (once) {
              this.priceEl.textContent = once.textContent.trim();
            }
          }
          if (this.compareEl) {
            this.compareEl.hidden = true;
          }
        }

        if (this.summaryEl) {
          const summaryText = parts.filter(Boolean).join(' \u2022 ');
          this.summaryEl.textContent = summaryText;
          this.summaryEl.hidden = !summaryText;
        }

        if (variant && this.imageEl) {
          const vid = String(variant.id);
          const scr = pi?.querySelector(
            'script[data-buy-area-variant-json="' + vid + '"]'
          );
          let v = variant;
          if (scr) {
            try {
              v = JSON.parse(scr.textContent || '{}');
            } catch (e) {
              /* use passed variant */
            }
          }
          const imgUrl = v.featured_image?.src || v.featured_media?.preview_image?.src;
          if (imgUrl) {
            this.imageEl.src = imgUrl;
            this.imageEl.alt = v.name || this.imageEl.alt;
          }
        }

        const mainBtn = this.getMainSubmitButton();
        const mainText = mainBtn?.querySelector('[data-add-to-cart-text]');
        if (this.submitBtn && mainBtn) {
          this.submitBtn.disabled = mainBtn.disabled;
        }
        if (this.submitText && mainText) {
          this.submitText.textContent = mainText.textContent.trim();
        }
      }
    }
  );
}
